import AttachmentIcon from '@mui/icons-material/Attachment';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import ActiveInactive from 'src/components/ActiveInactive';
import CategoryItem from 'src/components/CategoryItem';
import CommaSeparated from 'src/components/CommaSeparated';
import { Link } from 'src/components/Link';
import PriorityLabel from 'src/components/Piority';
import { ItemValueType } from 'src/types';
import { formattedUSDate } from 'src/utils/date';
import { sortArrayLengthFn, sortItemTypeFn, sortStringPropFn } from 'src/utils/sorting';

import ActiveInactiveIcon from '../../../assets/icons/ActiveInactive/ActiveInactiveIcon';
import { Asset } from '../../../models/assets/asset';
import { GRID_CHECKBOX_SELECTION_COL } from '../../Table/constants';
import { ColoredItemType } from '../../TypesElement/СoloredTypeItem';
import AttachmentList from '../components/AttachmentList';
import ActivityStatusEditor from '../components/EditAsset/ActivityStatus';
import ActivityUserEditing from '../components/EditAsset/ActivityUserEditing';
import { AssetDescription } from '../components/EditAsset/AssetDescription';
import { AssetText } from '../components/EditAsset/AssetText';
import PriorityEditor from '../components/EditAsset/PriorityEditing';
import { SelectAccountComponent } from '../components/EditAsset/SelectAccountComponent';
import { SelectAssetWaiverStatusComponent } from '../components/EditAsset/SelectAssetWaiverStatusComponent';
import { SelectCategoryComponent } from '../components/EditAsset/SelectCategoryComponent';
import { SelectContainerTypes } from '../components/EditAsset/SelectContainerTypes';
import {
  SelectDate,
  SelectWaiverFollowUpDate,
} from '../components/EditAsset/SelectDate';
import { SelectSubCategoryComponent } from '../components/EditAsset/SelectSubCategoryComponent';
import { SelectTypeComponent } from '../components/EditAsset/SelectTypeComponent';
import { DescriptionCell } from '../components/RenderCell/DescriptionCell';
import { TitleCell } from '../components/RenderCell/TitleCell';

export const columns: Array<
  | GridColDef<Asset>
  | {
      filterOptionSecondaryTextGetter?: (item: ItemValueType) => string;
    }
> = [
  {
    ...GRID_CHECKBOX_SELECTION_COL,
    type: 'boolean',
    disableColumnMenu: true,
  },
  {
    field: 'isActive',
    headerName: 'Inactive',
    sortable: false,
    filterable: false,
    headerAlign: 'center',
    disableColumnMenu: true,
    renderHeader: () => <ActiveInactive />,
    width: 45,
    renderCell: ({ value }) => (!value ? <ActiveInactiveIcon /> : null),
  },
  {
    field: 'totalAttachments',
    width: 120,
    minWidth: 120,
    headerName: 'Total Attachments',
    headerAlign: 'center',
    cellClassName: 'attachment-cell',
    renderHeader: () => (
      <AttachmentIcon
        fontSize="small"
        sx={{
          transform: 'rotate(-45deg)',
          color: 'text.secondary',
        }}
      />
    ),
    disableColumnMenu: true,
    renderCell: params => <AttachmentList asset={params.row} />,
  },
  {
    field: 'reference',
    headerName: 'Activity ID',
    disableColumnMenu: true,
    width: 130,
  },
  {
    field: 'minervaId',
    headerName: 'Minerva ID',
    disableColumnMenu: true,
    width: 170,
    renderCell: ({ value, row }) => (
      <Link to="account" params={{ id: row.account?.id || 0 }}>
        {value}
      </Link>
    ),
  },
  {
    field: 'generatorId',
    headerName: 'Generator ID',
    disableColumnMenu: true,
    width: 170,
    renderCell: ({ value, row }) => (
      <Link to="generator" params={{ id: row.generator?.id || 0 }}>
        {value}
      </Link>
    ),
  },
  {
    field: 'type',
    width: 200,
    headerName: 'Activity Type',
    editable: true,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderCell: ({ value }: GridRenderCellParams) => (
      <ColoredItemType id={value.id} name={value.name} flow="asset" />
    ),
    renderEditCell: ({ id, field, value }) => (
      <SelectTypeComponent id={id} value={value} field={field} />
    ),
  },
  {
    field: 'category',
    width: 200,
    headerName: 'Activity Category',
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderCell: ({ value }: GridRenderCellParams) => (
      <CategoryItem name={value.name} />
    ),
    editable: true,
    renderEditCell: ({ id, value, field }) => (
      <SelectCategoryComponent id={id} value={value} field={field} />
    ),
  },
  {
    field: 'subCategory',
    width: 200,
    editable: true,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderEditCell: ({ id, value, field }) => (
      <SelectSubCategoryComponent id={id} value={value} field={field} />
    ),
    headerName: 'Activity Sub-Category',
    renderCell: ({ value }: GridRenderCellParams) =>
      value?.name && <CategoryItem name={value?.name} />,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 200,
    editable: true,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderEditCell: ({ id, value, field }) => (
      <ActivityStatusEditor id={id} value={value} field={field} />
    ),
    renderCell: ({ value }) => value?.name || '',
  },
  {
    field: 'priority',
    headerName: 'Priority',
    width: 200,
    editable: true,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderEditCell: ({ id, value, field }) => (
      <PriorityEditor id={id} value={value} field={field} />
    ),
    renderCell: ({ value }) => (
      <PriorityLabel sx={{ justifyContent: 'flex-start' }} priority={value} />
    ),
  },
  {
    field: 'assetDate',
    headerName: 'Activity Date',
    width: 200,
    disableColumnMenu: true,
    editable: true,
    valueFormatter: ({ value }) => formattedUSDate(value),
    renderEditCell: ({ id, value, field }) => {
      return <SelectDate id={id} value={value} field={field} />;
    },
    renderCell: ({ value }) => formattedUSDate(value),
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    width: 200,
    disableColumnMenu: true,
    editable: true,
    valueFormatter: ({ value }) => formattedUSDate(value),
    renderEditCell: ({ id, value, field }) => {
      return <SelectDate id={id} value={value} field={field} />;
    },
    renderCell: ({ value }) => formattedUSDate(value),
  },
  {
    field: 'overdue',
    headerName: 'Overdue',
    width: 200,
    disableColumnMenu: false,
    editable: false,
    filterable: true,
    type: 'boolean',
    minWidth: 100,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) => {
      if (typeof(params.value) !== 'boolean') {
        return null; // blank cell for null or undefined
      }
      return params.value ? 'Yes' : 'No';
    },
  },
  {
    field: 'user',
    headerName: 'Assigned to',
    width: 280,
    disableColumnMenu: false,
    editable: true,
    filterable: true,
    sortComparator: sortItemTypeFn,
    filterOptionSecondaryTextGetter: item => (item as any)?.data?.email,
    renderEditCell: ({ id, value, field }) => (
      <ActivityUserEditing id={id} value={value} field={field} />
    ),
    renderCell: ({ value }) => (!value ? '' : value?.name),
  },
  {
    field: 'waiverInfo',
    width: 200,
    editable: true,
    filterable: true,
    sortComparator: (v1, v2, cellParams1, cellParams2) => sortItemTypeFn(v1?.status, v2?.status, cellParams1, cellParams2),
    renderEditCell: ({ id, value, field }) => (
      <SelectAssetWaiverStatusComponent id={id} value={value} field={field} />
    ),
    headerName: 'Waiver Status',
    renderCell: ({ value }: GridRenderCellParams) => {
      return value?.status.name;
    },
  },
  {
    field: 'containerTypes',
    minWidth: 280,
    editable: true,
    sortComparator: sortArrayLengthFn,
    renderEditCell: params => <SelectContainerTypes {...params} />,
    headerName: 'Low Population Waiver',
    renderCell: ({ value }) =>
      value ? <CommaSeparated types={value} /> : null,
  },
  {
    field: 'dateIssued',
    headerName: 'Date Issued',
    editable: true,
    type: 'date',
    width: 160,
    disableColumnMenu: true,
    valueFormatter: ({ value }) => formattedUSDate(value),
    renderEditCell: ({ id, value, field }) => {
      return <SelectDate id={id} value={value} field={field} />;
    },
    renderCell: ({ value }) => formattedUSDate(value),
  },
  {
    field: 'waiverInfoFollowUpDate',
    headerName: 'Waiver Follow-up Date',
    editable: true,
    type: 'date',
    width: 160,
    disableColumnMenu: true,
    valueFormatter: ({ value }) => formattedUSDate(value),
    renderEditCell: ({ id, value, field }) => (
      <SelectWaiverFollowUpDate id={id} value={value} field={field} />
    ),
    renderCell: ({ value }) => formattedUSDate(value),
  },
  {
    field: 'jurisdiction',
    headerName: 'Jurisdiction',
    width: 150,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderCell: ({ value }) => value?.name || '',
  },
  {
    field: 'hauler',
    headerName: 'Hauler',
    width: 120,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderCell: ({ value }) => value?.name || '',
  },
  {
    field: 'agency',
    headerName: 'Agency',
    sortable: false,
    minWidth: 120,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderCell: ({ value }) => value?.name || '',
  },
  {
    field: 'account',
    headerName: 'Account #',
    width: 170,
    editable: true,
    disableColumnMenu: true,
    sortComparator: sortStringPropFn('accountNumber'),
    renderEditCell: ({ id, row, field, value }) => (
      <SelectAccountComponent
        id={id}
        generatorId={row.generator?.id ?? null}
        value={value}
        field={field}
      />
    ),
    renderCell: ({ value }) => value?.accountNumber || '',
  },
  {
    field: 'accountType',
    headerName: 'Account Type',
    width: 120,
    filterable: true,
    sortComparator: sortItemTypeFn,
    renderCell: ({ value }) => value?.name || '',
  },
  {
    field: 'accountName',
    headerName: 'Account Name',
    width: 270,
    disableColumnMenu: true,
    sortComparator: sortItemTypeFn,
    valueGetter: ({ row }) => row.account,
    renderCell: ({ row }) => row?.account?.name || '',
  },
  {
    field: 'generator',
    headerName: 'Account Address',
    disableColumnMenu: true,
    width: 300,
    sortComparator: sortItemTypeFn,
    renderCell: ({ value }) => value?.name || '',
  },
  {
    field: 'createdAt',
    width: 145,
    disableColumnMenu: true,
    headerName: 'Created Date',
    valueFormatter: ({ value }) => formattedUSDate(value),
  },
  {
    field: 'createdBy',
    width: 200,
    editable: false,
    disableColumnMenu: true,
    headerName: 'Created By',
    valueGetter: ({ value }) => value.name,
  },
  {
    field: 'shortText',
    headerName: 'Activity Title',
    disableColumnMenu: true,
    width: 390,
    editable: true,
    renderCell: ({ id, hasFocus, row }) => (
      <TitleCell id={id} hasFocus={hasFocus} row={row} />
    ),
    renderEditCell: ({ id, field, hasFocus }) => (
      <AssetText id={id} field={field} hasFocus={hasFocus} />
    ),
  },
  {
    field: 'description',
    headerName: 'Activity Description',
    disableColumnMenu: true,
    width: 390,
    editable: true,
    renderCell: ({ id, hasFocus, row }) => (
      <DescriptionCell id={id} hasFocus={hasFocus} row={row} />
    ),
    renderEditCell: ({ id, field, hasFocus }) => (
      <AssetDescription id={id} field={field} hasFocus={hasFocus} />
    ),
  },
];
